import * as React from 'react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { Dropdown } from './Dropdown';
import { LineInfo } from './LineInfo';
import { Button } from './buttons/Button';
import { axios } from '../api';
import { Close } from '../assets/Close';
import { useShippingRates, ShippingOption } from '../hooks/useShippingRates';
import { Customer, Order } from '../pages/types';
import {
  BOTTOM_TOAST,
  generateErrorMsg,
  generateFullName,
  getCountryFlag,
  mapSntApiDate,
} from '../utils';

type ChangeShippingOptionModalProps = {
  customer: Customer;
  order: Order;
  refreshCustomer: () => void;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ChangeShippingOptionModal = ({
  customer,
  order,
  refreshCustomer,
  onCancel,
  onConfirm,
}: ChangeShippingOptionModalProps) => {
  const [selectedShippingOption, setSelectedShippingOption] = useState<ShippingOption>({
    value: '',
    label: 'Select shipping method',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { shippingOptions, isLoadingShippingRates } = useShippingRates(order.id, order.scheduled);

  useEffect(() => {
    const expeditedOption = shippingOptions.find(
      (option) => option.value === order.shipping_method
    );
    if (expeditedOption) {
      setSelectedShippingOption(expeditedOption);
    }
  }, [order.shipping_method, shippingOptions]);

  const handleSubmit = () => {
    setIsSubmitting(true);
    axios
      .patch(`orders/${order.id}/update_shipping_method`, {
        shipping_method: selectedShippingOption.rate,
      })
      .then(() => {
        const toastMsg = `Shipping method updated to ${selectedShippingOption.value}.`;
        toast.success(toastMsg, BOTTOM_TOAST);
        onConfirm();
        refreshCustomer();
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-6 min-w-[700px] relative border">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-left">Update shipping method</h1>
        <div className="space-y-0">
          <LineInfo
            title="Customer"
            value={`${getCountryFlag(customer.default_country)} ${generateFullName(
              customer.first_name,
              customer.last_name
            )} (${customer.email})`}
            className="mb-0"
          />
          <LineInfo title="Order" value={order.id} className="mb-0" />
          <LineInfo title="Order Date" value={mapSntApiDate(order.scheduled)} className="mb-0" />
        </div>
        <hr className="my-4" />
        <h3 className="font-bold text-left mb-1">Select a shipping method for this order:</h3>
        <div className="mb-4">
          {isLoadingShippingRates ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-gray-500">Loading shipping options...</div>
            </div>
          ) : (
            <Dropdown
              options={shippingOptions}
              selectedOption={selectedShippingOption}
              setSelectedOption={setSelectedShippingOption}
            />
          )}
        </div>
        <div className="mt-[15px] w-full flex justify-center">
          <Button
            label="Save Changes"
            variant="primary"
            isDisabled={!selectedShippingOption.value || isSubmitting || isLoadingShippingRates}
            onClick={() => handleSubmit()}
          />
        </div>
      </div>
    </div>
  );
};
